import * as React from 'react'
import { Upload, FileText, X, Clipboard, FileSpreadsheet, Binary, TrendingUp, Shield, AlertTriangle, ChevronDown, ChevronUp, Download } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useTestStore } from '@/stores/testStore'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { isMultiColumnText } from '@/lib/fileParser'
import { DataTransformer } from '@/components/DataTransformer'
import type { ParseResult } from '@/lib/fileParser'

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`
}

function formatFormatLabel(format: string): string {
  switch (format) {
    case 'ascii01': return 'ASCII 0/1'
    case 'raw-bytes': return 'Raw Binary'
    case 'hex': return 'Hex String'
    case 'csv': return 'CSV (integers)'
    case 'float-csv': return 'CSV (floats)'
    case 'float-tsv': return 'TSV (floats)'
    case 'float-space': return 'Space-separated (floats)'
    case 'binary': return 'Binary'
    default: return format
  }
}

function getPreview(result: ReturnType<typeof useTestStore.getState>['inputData']): string {
  if (!result) return ''
  const maxItems = 100

  if (result.kind === 'binary') {
    const { data, bitLength } = result.input
    const count = Math.min(bitLength, maxItems)
    const bits: string[] = []
    for (let i = 0; i < count; i++) {
      bits.push((data[i >>> 3] >> (7 - (i & 7))) & 1 ? '1' : '0')
    }
    const suffix = bitLength > maxItems ? `... (${bitLength} bits total)` : ''
    return bits.join('') + suffix
  }

  if (result.kind === 'symbol') {
    const count = Math.min(result.input.data.length, maxItems)
    const values = Array.from(result.input.data.slice(0, count))
    const suffix = result.input.data.length > maxItems ? ` ... (${result.input.data.length} values total)` : ''
    return values.join(', ') + suffix
  }

  if (result.kind === 'continuous') {
    const count = Math.min(result.input.data.length, maxItems)
    const values = Array.from(result.input.data.slice(0, count)).map((v) => v.toPrecision(6))
    const suffix = result.input.data.length > maxItems ? ` ... (${result.input.data.length} values total)` : ''
    return values.join(', ') + suffix
  }

  return ''
}

function makeTimestamp(): string {
  const now = new Date()
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}-${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`
}

function buildDownloadPayload(result: ParseResult): { content: string; extension: string } {
  if (result.kind === 'binary') {
    const { data, bitLength } = result.input
    const bits = new Array<string>(bitLength)
    for (let i = 0; i < bitLength; i++) {
      bits[i] = (data[i >>> 3] >> (7 - (i & 7))) & 1 ? '1' : '0'
    }
    return { content: bits.join(''), extension: 'txt' }
  }

  if (result.kind === 'symbol') {
    return { content: Array.from(result.input.data).join('\n'), extension: 'csv' }
  }

  return { content: Array.from(result.input.data).join('\n'), extension: 'csv' }
}

interface FileUploaderProps {
  acceptedKinds?: ('binary' | 'symbol' | 'continuous')[]
  kindMismatchMessage?: string
  formatHints?: 'binary' | 'continuous' | 'all'
  onDataLoaded?: (result: ParseResult) => void
  /** Per-module data: when provided, FileUploader displays this instead of shared store */
  moduleData?: ParseResult | null
  /** Called when user clears data in per-module mode */
  onModuleClear?: () => void
}

export function FileUploader({ acceptedKinds, kindMismatchMessage, formatHints = 'binary', onDataLoaded, moduleData, onModuleClear }: FileUploaderProps = {}) {
  const store = useTestStore()
  // Use per-module data if provided, otherwise fall back to shared store
  const inputData = moduleData !== undefined ? moduleData : store.inputData
  const inputFile = moduleData !== undefined ? null : store.inputFile
  const { isLoading, error, loadFile, loadText, setInputData } = store
  const reset = moduleData !== undefined ? (onModuleClear ?? store.reset) : store.reset
  const [isDragging, setIsDragging] = React.useState(false)
  const inputRef = React.useRef<HTMLInputElement>(null)
  const [sizeError, setSizeError] = React.useState<string | null>(null)
  const [transformerData, setTransformerData] = React.useState<{
    text: string; fileName: string; fileSize: number
  } | null>(null)
  const [isPreviewExpanded, setIsPreviewExpanded] = React.useState(false)

  const handleFile = React.useCallback(
    async (file: File) => {
      setSizeError(null)
      // Enforce file size limit (200 MB) to prevent OOM crashes
      const MAX_FILE_SIZE = 200 * 1024 * 1024
      if (file.size > MAX_FILE_SIZE) {
        setSizeError(`File too large (${formatBytes(file.size)}). Maximum supported size is ${formatBytes(MAX_FILE_SIZE)}.`)
        return
      }
      // Check if the file is multi-column structured data
      try {
        const buffer = await file.arrayBuffer()
        const check = isMultiColumnText(buffer, file.name)
        if (check.isMultiColumn) {
          setTransformerData({ text: check.text, fileName: file.name, fileSize: file.size })
          return
        }
      } catch {
        // Fall through to normal parsing
      }
      void loadFile(file)
    },
    [loadFile]
  )

  // Fire onDataLoaded callback whenever inputData changes
  const onDataLoadedRef = React.useRef(onDataLoaded)
  onDataLoadedRef.current = onDataLoaded
  const prevInputDataRef = React.useRef(inputData)
  React.useEffect(() => {
    if (inputData && inputData !== prevInputDataRef.current && onDataLoadedRef.current) {
      onDataLoadedRef.current(inputData)
    }
    prevInputDataRef.current = inputData
  }, [inputData])

  // In moduleData mode, loadFile updates the shared store but the component reads moduleData.
  // Bridge the gap: when store.inputData changes from a loadFile call, fire onDataLoaded so
  // the parent page can adopt the parsed result into its own state (which feeds back as moduleData).
  const prevStoreDataRef = React.useRef(store.inputData)
  React.useEffect(() => {
    if (moduleData !== undefined && store.inputData && store.inputData !== prevStoreDataRef.current && onDataLoadedRef.current) {
      onDataLoadedRef.current(store.inputData)
    }
    prevStoreDataRef.current = store.inputData
  }, [store.inputData, moduleData])

  React.useEffect(() => {
    // Keep preview collapsed by default for each newly loaded dataset.
    setIsPreviewExpanded(false)
  }, [inputData])

  const handleTransformComplete = React.useCallback(
    (result: ParseResult) => {
      setInputData(result)
      setTransformerData(null)
    },
    [setInputData]
  )

  const handleTransformCancel = React.useCallback(() => {
    setTransformerData(null)
  }, [])

  const handleDownloadCurrent = React.useCallback(() => {
    if (!inputData) return
    const { content, extension } = buildDownloadPayload(inputData)
    const stem = (inputData.input.fileName || 'input-data').replace(/\.[^/.]+$/, '')
    const filename = `${stem}-${makeTimestamp()}.${extension}`
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }, [inputData])

  const handleDrop = React.useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      setIsDragging(false)
      const file = e.dataTransfer.files[0]
      if (file) handleFile(file)
    },
    [handleFile]
  )

  const handleDragOver = React.useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }, [])

  const handleDragLeave = React.useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
  }, [])

  const handleInputChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (file) handleFile(file)
    },
    [handleFile]
  )

  // Paste support
  React.useEffect(() => {
    const handlePaste = (e: ClipboardEvent) => {
      // Check for pasted files
      const file = e.clipboardData?.files[0]
      if (file) {
        handleFile(file)
        return
      }

      // Check for pasted text (with size limit to prevent OOM)
      const text = e.clipboardData?.getData('text')
      const MAX_PASTE_SIZE = 200 * 1024 * 1024 // 200 MB
      if (text && text.trim().length > 0) {
        if (text.length > MAX_PASTE_SIZE) {
          setSizeError(`Pasted data too large (${formatBytes(text.length)}). Maximum supported size is ${formatBytes(MAX_PASTE_SIZE)}.`)
          return
        }
        setSizeError(null)
        loadText(text.trim())
      }
    }

    document.addEventListener('paste', handlePaste)
    return () => document.removeEventListener('paste', handlePaste)
  }, [handleFile, loadText])

  // Data Transformer wizard (multi-column data detected)
  if (transformerData) {
    return (
      <DataTransformer
        rawText={transformerData.text}
        fileName={transformerData.fileName}
        fileSize={transformerData.fileSize}
        onComplete={handleTransformComplete}
        onCancel={handleTransformCancel}
      />
    )
  }

  // Size error inline message
  if (sizeError && !inputData && !isLoading) {
    return (
      <Card className="border-destructive/50">
        <CardContent className="p-6">
          <div className="flex flex-col items-center gap-3 py-4">
            <AlertTriangle className="h-6 w-6 text-destructive" />
            <p className="text-sm text-destructive text-center">{sizeError}</p>
            <Button variant="outline" size="sm" onClick={() => setSizeError(null)}>
              Try again
            </Button>
          </div>
        </CardContent>
      </Card>
    )
  }

  // Empty/upload state
  if (!inputData && !isLoading && !error) {
    return (
      <Card>
        <CardContent className="p-6">
          <div
            role="button"
            tabIndex={0}
            aria-label="Upload data file. Drop a file here, click to browse, or paste with Ctrl+V"
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={() => inputRef.current?.click()}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                inputRef.current?.click()
              }
            }}
            className={cn(
              'flex flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed p-8 text-center cursor-pointer transition-colors',
              isDragging
                ? 'border-primary bg-primary/5'
                : 'border-muted-foreground/25 hover:border-primary/50 hover:bg-accent/50'
            )}
          >
            <Upload className="h-10 w-10 text-muted-foreground" />
            <div>
              <p className="text-sm font-medium">
                Drop any file here, click to browse, or paste (Ctrl+V)
              </p>
              <p className="mt-2 text-xs text-muted-foreground max-w-md">
                We accept almost any data format — the system will auto-detect and guide you through conversion if needed.
              </p>
            </div>

            {/* Format hints */}
            <div className="flex flex-wrap gap-2 mt-3 w-full max-w-lg">
              {(formatHints === 'binary' || formatHints === 'all') && (
                <div className="flex flex-col items-center text-center gap-1 rounded-md bg-muted/40 px-3 py-2 flex-1 min-w-[120px]">
                  <Binary className="h-3.5 w-3.5 text-primary shrink-0" />
                  <div>
                    <p className="text-[11px] font-medium">Binary</p>
                    <p className="text-[10px] text-muted-foreground">0/1 text, .bin, hex</p>
                  </div>
                </div>
              )}
              {(formatHints === 'binary' || formatHints === 'all') && (
                <div className="flex flex-col items-center text-center gap-1 rounded-md bg-muted/40 px-3 py-2 flex-1 min-w-[120px]">
                  <FileSpreadsheet className="h-3.5 w-3.5 text-primary shrink-0" />
                  <div>
                    <p className="text-[11px] font-medium">Multi-column</p>
                    <p className="text-[10px] text-muted-foreground">CSV, TSV — select column & set rules</p>
                  </div>
                </div>
              )}
              {(formatHints === 'continuous' || formatHints === 'all') && (
                <div className="flex flex-col items-center text-center gap-1 rounded-md bg-muted/40 px-3 py-2 flex-1 min-w-[120px]">
                  <TrendingUp className="h-3.5 w-3.5 text-primary shrink-0" />
                  <div>
                    <p className="text-[11px] font-medium">Continuous</p>
                    <p className="text-[10px] text-muted-foreground">Float values for Gaussian tests</p>
                  </div>
                </div>
              )}
            </div>

            {/* Reassurance + paste hint */}
            <div className="flex flex-col items-center gap-1.5 mt-2">
              <div className="flex items-start gap-1.5">
                <Shield className="h-3 w-3 text-green-500 mt-0.5 shrink-0" />
                <span className="text-[10px] text-muted-foreground">All processing happens locally — your data never leaves this browser</span>
              </div>
              <div className="flex items-start gap-1.5">
                <Clipboard className="h-3 w-3 text-muted-foreground mt-0.5 shrink-0" />
                <span className="text-[10px] text-muted-foreground">You can also paste data directly with Ctrl+V / Cmd+V</span>
              </div>
            </div>
          </div>
          <input
            ref={inputRef}
            type="file"
            className="hidden"
            onChange={handleInputChange}
            data-testid="file-input"
          />
        </CardContent>
      </Card>
    )
  }

  // Loading state
  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center gap-3 py-8">
            <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
            <span className="text-sm text-muted-foreground">Parsing file...</span>
          </div>
        </CardContent>
      </Card>
    )
  }

  // Error state
  if (error) {
    return (
      <Card className="border-destructive/50">
        <CardContent className="p-6">
          <div className="flex flex-col items-center gap-3 py-4">
            <p className="text-sm text-destructive">{error}</p>
            <Button variant="outline" size="sm" onClick={reset}>
              Try again
            </Button>
          </div>
        </CardContent>
      </Card>
    )
  }

  // Loaded state with stats and preview
  const preview = getPreview(inputData)
  const info = inputData
    ? inputData.kind === 'binary'
      ? inputData.input
      : inputData.kind === 'symbol'
        ? inputData.input
        : inputData.input
    : null

  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3 min-w-0">
            <FileText className="h-8 w-8 text-primary shrink-0 mt-0.5" />
            <div className="min-w-0">
              <p className="text-sm font-medium truncate">
                {inputFile?.name ?? info?.fileName ?? 'Pasted text'}
              </p>
              <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground" data-testid="file-info">
                {info && (
                  <>
                    <span>Size: {formatBytes(info.fileSize)}</span>
                    <span data-testid="sample-count">Samples: {info.sampleCount.toLocaleString()}</span>
                    <span>Format: {formatFormatLabel(info.format)}</span>
                    <span>Kind: {inputData?.kind ?? '---'}</span>
                  </>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              onClick={handleDownloadCurrent}
              aria-label="Download current data"
            >
              <Download className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={reset}
              aria-label="Clear file"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Data preview */}
        {preview && (
          <div className="mt-4">
            <button
              type="button"
              onClick={() => setIsPreviewExpanded((prev) => !prev)}
              className="w-full flex items-center justify-between text-xs font-medium text-muted-foreground mb-1 hover:text-foreground transition-colors"
              aria-expanded={isPreviewExpanded}
            >
              <span>Preview (first 100 values)</span>
              {isPreviewExpanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
            </button>
            <div className="rounded-md bg-muted/50 p-3">
              <code
                className={cn(
                  'block text-xs font-mono break-all',
                  !isPreviewExpanded && 'line-clamp-3'
                )}
              >
                {preview}
              </code>
            </div>
          </div>
        )}

        {/* Kind mismatch warning */}
        {inputData && acceptedKinds && acceptedKinds.length > 0 && !acceptedKinds.includes(inputData.kind) && (
          <div className="mt-4 flex items-start gap-2 rounded-md bg-yellow-500/10 border border-yellow-500/30 p-3">
            <AlertTriangle className="h-4 w-4 text-yellow-600 dark:text-yellow-400 shrink-0 mt-0.5" />
            <p className="text-xs text-yellow-600 dark:text-yellow-400">
              {kindMismatchMessage ?? `Loaded data type "${inputData.kind}" is not supported by this module. Expected: ${acceptedKinds.join(' or ')}.`}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
